import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import type { AlertKind, AlertSeverity } from '@fitting-room/contracts'
import { Chip } from '../../components/admin/Chip'
import { PageHeader } from '../../components/admin/PageHeader'
import { AlertRow } from '../../components/admin/orders/AlertList'
import { useAlerts } from '../../lib/adminQueries'
import { ALERT_KIND_LABEL, ALERT_KIND_ORDER } from '../../lib/fulfillment'
import { useStaffToken } from '../../lib/staffToken'

const GROUP_TITLE: Record<AlertSeverity, string> = {
  critical: 'Critical',
  warning: 'To get to',
}

/**
 * Alerts: everything the back office says needs doing, worked out on every
 * read from the orders and the follow-ups (`docs/22` § "Alerts"). After
 * tperent's quick views on its orders tab — "today's work" — each row says
 * why it is there, with the dates in it, and opens what it is about.
 *
 * Critical first — a gown not ordered, late, or not home with the wedding
 * close — then the rest, each group the longest-due first, as the API sorts
 * them. The kind chips narrow it, and live in the URL so a filtered list
 * survives a reload and can be sent to a colleague.
 */
export function AlertsPage() {
  const { token } = useStaffToken()
  const [params, setParams] = useSearchParams()
  const query = useAlerts(token)
  const kind = params.get('kind') as AlertKind | null
  const alerts = useMemo(() => (query.data?.alerts ?? []).filter((alert) => !kind || alert.kind === kind), [query.data, kind])
  const groups = useMemo(
    () =>
      (['critical', 'warning'] as const)
        .map((severity) => ({ severity, rows: alerts.filter((alert) => alert.severity === severity) }))
        .filter((group) => group.rows.length > 0),
    [alerts],
  )
  const data = query.data
  const setKind = (next: AlertKind | null) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev)
        if (next) out.set('kind', next)
        else out.delete('kind')
        return out
      },
      { replace: true },
    )

  return (
    <div>
      <PageHeader eyebrow="What needs doing" title="Alerts">
        {query.data && query.data.critical > 0 ? <span className="st-badge st-badge--alert">{query.data.critical} critical</span> : null}
      </PageHeader>

      {data && data.total > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by kind">
          <Chip pressed={!kind} onClick={() => setKind(null)} count={data.total}>
            All
          </Chip>
          {ALERT_KIND_ORDER.filter((k) => data.counts[k] > 0 || k === kind).map((k) => (
            <Chip key={k} pressed={kind === k} onClick={() => setKind(kind === k ? null : k)} count={data.counts[k]}>
              {ALERT_KIND_LABEL[k]}
            </Chip>
          ))}
        </div>
      ) : null}

      {query.isError ? <p className="st-alert mb-4">Couldn&rsquo;t load the alerts. Try again in a moment.</p> : null}
      {query.isPending ? <p className="st-meta">Loading…</p> : null}

      {query.data && alerts.length === 0 ? (
        <div className="st-card px-4 py-10 text-center">
          <p className="st-meta">
            {kind ? 'None of these right now.' : 'Nothing needs doing. Every gown is on track and every follow-up is done.'}
          </p>
        </div>
      ) : null}

      {groups.map((group) => (
        <section key={group.severity} className="mb-6">
          <h2 className="st-eyebrow mb-2" style={group.severity === 'critical' ? { color: '#96432f' } : undefined}>
            {GROUP_TITLE[group.severity]} <span className="tabular-nums">{group.rows.length}</span>
          </h2>
          {group.rows.map((alert) => (
            <AlertRow key={alert.id} alert={alert} />
          ))}
        </section>
      ))}

      {query.data ? (
        <p className="st-meta mt-2 text-xs">
          The thresholds behind these are under Settings. An alert goes away by itself once what it&rsquo;s about is done.
        </p>
      ) : null}
    </div>
  )
}
